import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "ml-auto" }

import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import Menu from 'primevue/menu';
import { useToast } from 'primevue/usetoast';
import { OrganizationInterface, OrganizationsInterface, OrganizationTableInterface } from '@/types';
import AddOrganizationModal from '@/components/AddOrganizationModal/AddOrganizationModal.vue';
import OrganizationConnections from './OrganizationConnections.vue';
import { useStudentOrganizationStore } from '@/store';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface OrganizationPropsInterface {
  tableData?: OrganizationInterface[];
  studentId?: number | undefined;
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'StudentOrganizationTable',
  props: {
    tableData: {},
    studentId: {},
    loading: { type: Boolean }
  },
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;
const organizationStudentStore = useStudentOrganizationStore();
const organizations: Ref<OrganizationInterface[] | undefined> = toRef(props, 'tableData');
const organizationCount: Ref<number> = computed(() => (organizations.value ? organizations.value.length : 0));
const organizationModalOpen = ref(false);
const toast = useToast();
const organizationItems: Ref<OrganizationsInterface[]> = ref([]);

const createMenuModel = (item: OrganizationTableInterface) => {
  const last = item[item.length - 1];
  const id = last.id;
  const name = last.name;
  const menuItem = {
    label: 'Remove Organization',
    command: () => detachOrganization(id, name),
    visible: can([PERMISSIONS.STUDENT_DETACH_ORGANIZATIONS]),
  };
  return [menuItem].filter((item) => item.visible);
};

const attachOrganization: (idsitem: number[]) => Promise<void> = async (ids) => {
  await organizationStudentStore
    .attachOrganizations({
      organizations: ids,
      studentId: props.studentId as number,
    })
    .then(() => {
      emit(EMIT.UPDATE);
      toast.add({
        severity: 'success',
        summary: 'Added',
        detail: 'Organization has been added',
        life: 3000,
      });
    })
    .catch((response) => {
      let detail = 'Student already belongs to an organization';
      if (response.data.errors.external_id) {
        detail = response.data.errors.external_id[0];
      }
      toast.add({
        severity: 'error',
        summary: 'Student could not be added to an organization',
        detail: detail,
        life: 3000,
      });
    });
};

const detachOrganization: (id: number[], organizationName: string) => Promise<void> = async (ids, organizationName) => {
  await organizationStudentStore.detachOrganizations({ organizations: [ids], studentId: props.studentId as number });
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail: `Organization ${organizationName} has been removed from student`,
    life: 3000,
  });
  emit(EMIT.UPDATE);
};

const openAddOrganizations = async () => {
  if (can([PERMISSIONS.STUDENT_ORGANIZATIONS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.STUDENT_ATTACH_ORGANIZATIONS])) {
    organizationItems.value = await organizationStudentStore.getAddOrganizationsModalData(props.studentId as number);
    organizationModalOpen.value = true;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add organizations',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (organizationModalOpen.value = false);

const onSaveAdd = (organizations: number[]) => {
  attachOrganization(organizations);
  organizationModalOpen.value = false;
};

const menu = ref();

const onToggle = (event: Event): void => menu.value.toggle(event);

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Column = _resolveComponent("Column")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_DataTable = _resolveComponent("DataTable")!
  const _component_Card = _resolveComponent("Card")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _cache[0] || (_cache[0] = _createElementVNode("h2", null, "Organizations", -1)),
      _createVNode(_unref(Chip), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(organizationCount.value), 1)
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        (_unref(can)([_unref(PERMISSIONS).STUDENT_ATTACH_ORGANIZATIONS]))
          ? (_openBlock(), _createBlock(_component_AppButton, {
              key: 0,
              type: "text",
              size: "small",
              text: "Add Organization",
              "icon-left": "add",
              onClick: openAddOrganizations
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    content: _withCtx(() => [
      _createVNode(_component_DataTable, {
        value: organizations.value,
        "global-filter-fields": ['name'],
        scrollable: true,
        "loading-icon": "logo-spinner",
        loading: __props.loading,
        "data-key": "name",
        "responsive-layout": "scroll",
        "scroll-height": "440px",
        "state-key": "session-student-organization-store-table",
        "state-storage": "session",
        class: _normalizeClass({ 'table-loading': __props.loading })
      }, {
        empty: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Organizations not assigned", -1)
        ]))]),
        default: _withCtx(() => [
          (organizationCount.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_Column, {
                  "body-class": "w-full",
                  "header-class": "w-full",
                  field: "name",
                  header: "Organization"
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(OrganizationConnections, { organizations: data }, null, 8, ["organizations"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_Column, {
                  "body-class": "justify-content-center mw-10rem",
                  "header-class": "justify-content-center mw-10rem",
                  field: "id",
                  header: "Actions"
                }, {
                  body: _withCtx(({ data }) => [
                    _createVNode(_component_Icon, {
                      name: "more_horiz",
                      class: "text-lg p-1 cursor-pointer",
                      onClick: onToggle
                    }),
                    _createVNode(_unref(Menu), {
                      ref_key: "menu",
                      ref: menu,
                      model: createMenuModel(data),
                      popup: true
                    }, null, 8, ["model"])
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["value", "loading", "class"]),
      (organizationModalOpen.value && _unref(can)([_unref(PERMISSIONS).STUDENT_ORGANIZATIONS_AVAILABLE_TO_ADD]))
        ? (_openBlock(), _createBlock(AddOrganizationModal, {
            key: 0,
            title: "Add Organization",
            items: organizationItems.value,
            searchable: true,
            "multi-selection": false,
            onSave: onSaveAdd,
            onCancel: onCancelAdd
          }, null, 8, ["items"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})